<template>
  <div class="pt-0 hyp-content-details-container">
    <div v-if="!isMinimized">
      <div class="details-container">
        <v-row>
          <v-col cols="6" class="pt-0 col col-6 pl-6">
            <h1>
              <v-icon
                v-if="!isMinimized"
                size="18"
                @click="handleClickMinimize"
                title="Hide results"
                class="mr-3"
                >fas fa-arrow-left</v-icon
              >
              <span>
                {{ selectedLocation.name }} Cities ({{ cities.length }})
              </span>
            </h1>
          </v-col>
          <v-col cols="6" class="pt-0 pr-6 col col-6 text-right">
            <RisksReport
              class="pl-2 pull-right"
              v-if="selectedCity && isCityReportFeatureUnlocked"
              :selected-location="selectedCity"
            ></RisksReport>
            <v-btn
              @click="openLocationWatch"
              color="primary"
              v-if="!hasEssentials"
              small
            >
              <span>Watch Location</span>
            </v-btn>
          </v-col>
        </v-row>

        <v-sheet>
          <v-btn
            class="pull-left text-capitalize hyp-back-to-country-btn"
            x-large
            color="success"
            plain
            text
            @click="handleClickBackToCountry"
          >
            Back to {{ selectedLocation.name }}
          </v-btn>
          <v-tabs
            v-model="selectedCityCode"
            next-icon="mdi-arrow-right-bold-box-outline"
            prev-icon="mdi-arrow-left-bold-box-outline"
            show-arrows
          >
            <v-tab
              v-for="item in cities"
              :key="item.code"
              :href="'#' + item.code"
              class="hyp-risk-tabs"
              @click="handleChangeCity(item)"
            >
              {{ item.name }}
            </v-tab>
          </v-tabs>
        </v-sheet>

        <Loader
          v-if="loading"
          :text="loaderText"
          size="small"
          class="mt-5"
        ></Loader>
        <v-row v-if="!cityDetails && !hasContent && !loading">
          <v-col class="col-12 pa-4">
            The information for {{ this.selectedCity.name }} is not available
            within Seerist. Please check back periodically as we continue to
            update our city coverage.
          </v-col>
        </v-row>
        <v-container v-if="cityDetails" class="hyp-risk-details-container">
          <v-row>
            <v-col cols="12" class="pt-0">
              <v-container class="hyp-risk-tab-items mt-3">
                <v-row id="hyp-risk-grid">
                  <v-col
                    id="hyp-risk-content-wrapper"
                    cols="12"
                    class="pt-0 content-container hyp-risk-grid-2"
                  >
                    <div class="hyp-risk-grid-2-left">
                      <div class="hyp-risk-grid-2-leftgrid">
                        <RisksContentContainerWrapper
                          class="mb-2 pa-3 hyp-risk-grid-2-left1"
                          wrapper-id="risks-city-facts"
                          :reset-expanded="resetExpanded"
                          @click-expand="handleClickExpand"
                        >
                          <div>
                            <div class="hyp-grid-details-container">
                              <div
                                class="details-heading application-heading mb-1"
                              >
                                {{ cityDetails.name }} Facts
                                <SimpleDialog
                                  dialog-class="float-right"
                                  :show-sidebar="true"
                                >
                                  <template v-slot:dialogButton>
                                    <v-btn
                                      class="pull-right"
                                      color="success"
                                      outlined
                                      plain
                                      x-small
                                      rounded
                                    >
                                      <v-icon left x-small>
                                        fas fa-info-circle
                                      </v-icon>
                                      Our Methodology
                                    </v-btn>
                                  </template>

                                  <template>
                                    <RisksProfileBody
                                      v-if="cityMethodology"
                                      :profile-info="cityMethodology"
                                    ></RisksProfileBody>
                                  </template>
                                </SimpleDialog>
                              </div>

                              <div v-if="citySnapshot">
                                <v-simple-table dense>
                                  <template v-slot:default>
                                    <tbody>
                                      <tr
                                        v-for="item in citySnapshot"
                                        :key="item[0]"
                                      >
                                        <td class="text-capitalize">
                                          {{ item[0] }}
                                        </td>
                                        <td>{{ item[1] }}</td>
                                      </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>
                            </div>
                          </div>
                        </RisksContentContainerWrapper>
                        <RisksContentContainerWrapper
                          class="mb-2 pa-3 hyp-risk-grid-2-left1"
                          wrapper-id="risks-city-overview"
                          :reset-expanded="resetExpanded"
                          @click-expand="handleClickExpand"
                        >
                          <RisksProfileBody
                            v-if="cityOverview"
                            :profile-info="cityOverview"
                            container-class="hyp-grid-profile-container-sm"
                          ></RisksProfileBody>
                        </RisksContentContainerWrapper>
                        <RisksContentContainerWrapper
                          class="mb-2 pa-3 hyp-risk-grid-2-left1"
                          wrapper-id="risks-city-background"
                          :reset-expanded="resetExpanded"
                          @click-expand="handleClickExpand"
                        >
                          <RisksProfileBody
                            v-if="cityBackground"
                            :profile-info="cityBackground"
                            container-class="hyp-grid-profile-container"
                          ></RisksProfileBody>
                        </RisksContentContainerWrapper>
                      </div>
                    </div>
                    <RisksContentContainerWrapper
                      class="mb-2 hyp-risk-grid-2-right"
                      :wrapper-class="'pt-3 pr-3'"
                      wrapper-id="risks-pulse-ratings"
                      :reset-expanded="resetExpanded"
                      @click-expand="handleClickExpand"
                    >
                      <RisksPulseRatings
                        v-if="selectedCity"
                        :selected-location="selectedCity"
                        :risksContentExpanded="risksPulseRatingsExpanded"
                        :params="params"
                        :selected-primary-risk-category="
                          selectedPrimaryRiskCategory
                        "
                      ></RisksPulseRatings>
                    </RisksContentContainerWrapper>
                    <RisksContentContainerWrapper
                      v-if="cityImage && cityImage.url"
                      class="mb-2 pa-3 hyp-risk-grid-2-right1"
                      wrapper-id="risks-city-image"
                      :reset-expanded="resetExpanded"
                      @click-expand="handleClickExpand"
                    >
                      <div
                        class="hyp-grid-details-container hyp-grid-image-container"
                      >
                        <img
                          class="hyp-risks-city-image"
                          :src="cityImage.url"
                          :alt="cityImage.alternativeText"
                        />
                      </div>
                    </RisksContentContainerWrapper>
                    <RisksContentContainerWrapper
                      v-if="cityArrivals"
                      class="mb-2 pa-3 hyp-risk-grid-2-right2"
                      wrapper-id="risks-city-arrivals"
                      :reset-expanded="resetExpanded"
                      @click-expand="handleClickExpand"
                    >
                      <div class="details-heading application-heading">
                        Arrivals
                        <SimpleDialog
                          dialog-class="float-right"
                          :show-sidebar="true"
                        >
                          <template v-slot:dialogButton>
                            <v-btn
                              class="pull-right"
                              color="success"
                              outlined
                              plain
                              x-small
                              rounded
                            >
                              <v-icon left x-small>
                                fas fa-info-circle
                              </v-icon>
                              Our Methodology
                            </v-btn>
                          </template>

                          <template>
                            <RisksProfileBody
                              v-if="cityMethodology"
                              :profile-info="cityMethodology"
                            ></RisksProfileBody>
                          </template>
                        </SimpleDialog>
                      </div>
                      <v-container class="ma-0 pa-0">
                        <v-row class="ma-0 pa-0">
                          <v-col
                            v-for="(arrival, i) in cityArrivals"
                            :key="i"
                            cols="auto"
                            class="ml-0 mr-2 mb-2 mt-0 pa-0"
                          >
                            <v-card
                              class="my-0 mx-0 pa-2 mx-auto hyp-risks-city-arrivals-cards"
                              outlined
                            >
                              <v-card-text class="ma-0 pa-0">
                                <div>
                                  <div class="text-capitalize pull-left">
                                    <h3>
                                      <strong>{{ arrival.title }}</strong>
                                    </h3>
                                  </div>
                                  <div class="text-overline pull-right">
                                    <v-card
                                      flat
                                      width="60px"
                                      v-for="(r, index) in arrival.ratings"
                                      :key="'r' + index"
                                      class="pull-left hyp-risks-city-arrival-rating-cards"
                                      :class="{ 'hyp-hide': !r.rating }"
                                      :title="r.displayTitle"
                                    >
                                      <v-card-text
                                        class="mx-1 pa-0 text-center"
                                      >
                                        <i
                                          class="fa fa-lg"
                                          :class="getArrivalRatingIconClass(r)"
                                        ></i>
                                        <div class="text-capitalize">
                                          {{ r.rating }}
                                        </div>
                                      </v-card-text>
                                    </v-card>
                                  </div>
                                  <div class="clearfix">
                                    {{ arrival.description }}
                                  </div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-col>
                        </v-row>
                      </v-container>
                    </RisksContentContainerWrapper>
                    <RisksCalendarEvents
                      v-if="isCalendarEventsCitiesFeatureUnlocked"
                      :selected-location="getSelectedLocation"
                      :expand-left="false"
                    ></RisksCalendarEvents>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";

import {
  RISK_RATING_PRIMARIES,
  ARRIVAL_RATING_TYPES,
  ARRIVAL_RATING_LEVELS,
} from "@/common/constants/controlRisks/risks";
import {
  getCityDetails,
  getRiskRatingsByCityCode,
} from "@/services/controlRisks/risks.service";
import { hasEssentialsAccess } from "@/services/user/user.service";
import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import { MODULE_PREFIX_HOME } from "@/store";
import { GET_DETAILS_SIDEBAR } from "@/store/actions.type";
import { mapGetters } from "vuex";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { getCityMethodology } from "@/services/common/methodology.service";

const EMITTED_ACTIONS = {
  CLICK_CLOSE: "click-close",
  CLICK_MINIMIZE: "click-minimize",
  CLICK_BACK_TO_COUNTRY: "click-back-to-country",
};

export default {
  name: "RisksCitiesContent",
  components: {
    Loader: () => import("@/components/common/Loader"),
    RisksReport: () => import("@/components/controlRisks/risks/RisksReport"),
    RisksContentContainerWrapper: () =>
      import("@/components/controlRisks/risks/RisksContentContainerWrapper"),
    RisksPulseRatings: () =>
      import("@/components/controlRisks/risks/RisksPulseRatings"),
    RisksProfileBody: () =>
      import("@/components/controlRisks/risks/RisksProfileBody"),
    SimpleDialog: () => import("@/components/common/dialogs/SimpleDialog"),
    RisksCalendarEvents: () =>
      import("@/components/controlRisks/risks/RisksCalendarEvents"),
  },
  props: {
    selectedLocation: {
      type: Object,
    },
    cities: {
      type: Array,
      required: true,
      default: () => [],
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => {
        return { hidePulse: false };
      },
    },
  },
  data() {
    return {
      hasEssentials: hasEssentialsAccess(),
      resetExpanded: new Date().toISOString(),
      selectedCity: null,
      selectedCityCode: null,
      cityDetails: null,
      ratings: null,
      risksPulseRatingsExpanded: null,
      showMethodology: false,
      cityMethodology: null,
      hasContent: true,
      loading: false,
    };
  },
  async mounted() {
    this.cityMethodology = await getCityMethodology();
  },
  computed: {
    ...mapGetters({
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    isCityReportFeatureUnlocked() {
      return this.featureUnlocked("risks-geography-city-report");
    },
    isCalendarEventsCitiesFeatureUnlocked() {
      return this.featureUnlocked("calendar-events-cities");
    },
    selectedPrimaryRiskCategory() {
      return lodashFind(this.ratings, [
        "riskCategoryId",
        RISK_RATING_PRIMARIES.security,
      ]);
    },
    loaderText() {
      const selectedText =
        this.selectedCity && this.selectedCity.name
          ? this.selectedCity.name
          : "";
      return `Loading ${selectedText} details`;
    },
    getSelectedLocation() {
      let selectedLocation = lodashCloneDeep(this.selectedLocation);
      selectedLocation.selectedCity = this.selectedCity;
      return selectedLocation;
    },
  },
  methods: {
    handleClickClose() {
      this.$emit(EMITTED_ACTIONS.CLICK_CLOSE);
    },
    handleClickMinimize() {
      this.$emit(EMITTED_ACTIONS.CLICK_MINIMIZE);
    },
    handleClickBackToCountry() {
      this.$emit(EMITTED_ACTIONS.CLICK_BACK_TO_COUNTRY);
    },
    handleClickExpand(wrapperId, expandedYN) {
      this.risksPulseRatingsExpanded = null;
      if (wrapperId === "risks-pulse-ratings") {
        this.risksPulseRatingsExpanded = expandedYN ? wrapperId : null;
      }
    },
    openLocationWatch() {
      this.handleClickClose();

      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
      this.detailsSidebar.monitorType = DETAIL_SIDEBAR_TYPES.stability;
      this.detailsSidebar.stabilityType = GRIDS_STABILITY_TYPES.city;
      this.detailsSidebar.code = this.selectedCityCode;
      this.detailsSidebar.openMonitor = true;

      this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );
    },
    handleChangeCity(item) {
      this.loading = true;
      if (item) {
        item.type = "city";
        this.selectedCity = item;
        this.selectedCityCode = item.code;
      }
      this.getContent();
      this.getRatings();
    },
    async getContent() {
      this.cityDetails = null;
      try {
        this.cityDetails = (
          (await getCityDetails(this.selectedCityCode)) || {}
        )?.properties?.formatted;
        if (!this.cityDetails) {
          this.hasContent = false;
          return false;
        }
      } catch (error) {
        console.error(
          "There was an error trying to retrieve the data for this city: " +
            this.selectedCityCode
        );
      }

      this.citySnapshot = Object.entries(this.cityDetails?.snapshot).filter(
        ([key, value]) => key != "id"
      );

      this.cityOverview = lodashFind(this.cityDetails?.overview, [
        "type",
        "overview",
      ]);
      this.cityBackground = lodashFind(this.cityDetails?.overview, [
        "type",
        "background",
      ]);
      this.cityImage = this.cityDetails?.image;
      this.cityArrivals = this.cityDetails?.arrivals;

      return true;
    },
    async getRatings() {
      try {
        const { cities } = (await getRiskRatingsByCityCode(
          this.selectedCityCode
        )) || { cities: null };
        if (!cities) {
          this.hasContent = false;
          return false;
        }

        if (
          cities &&
          Array.isArray(cities) &&
          cities.length &&
          lodashGet(cities, "[0].properties.ratings")
        ) {
          this.ratings = cities[0].properties.ratings;
        }
      } catch (error) {
        console.error(
          "There was an error trying to retrieve risk rating data for this city: " +
            this.selectedCityCode
        );
      } finally {
        this.loading = false;
      }

      return true;
    },
    getArrivalRatingIconClass(rating) {
      return (
        " " +
        lodashFind(ARRIVAL_RATING_TYPES, ["type", rating.type?.toLowerCase()])
          ?.iconClass +
        " " +
        lodashFind(ARRIVAL_RATING_LEVELS, [
          "value",
          rating.rating?.toLowerCase(),
        ])?.colorClass +
        " "
      );
    },
  },
  watch: {
    selectedLocation: {
      async handler() {
        this.cityDetails = null;
        if (this.selectedLocation && this.selectedLocation.iso2Code) {
          this.selectedCityCode = this.selectedLocation.selectedCity?.code
            ? this.selectedLocation.selectedCity?.code
            : this.cities[0]?.code;
          let selectedCity = lodashFind(this.cities, [
            "code",
            this.selectedCityCode,
          ]);
          selectedCity.type = "city";
          this.selectedCity = selectedCity;
          await this.handleChangeCity();
          this.risksPulseRatingsExpanded = null;
          this.resetExpanded = new Date().toISOString();
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style>
.content-container img {
  max-width: 460px;
}
</style>

<style scoped>
.hyp-risks-content-expanded .hyp-grid-details-container {
  max-width: 50vw;
  margin: 12px auto;
}
.hyp-risk-details-container {
  margin: 0;
  padding: 0;
  max-width: unset !important;
  line-height: 1.5;
}
.hyp-risk-tab-items {
  height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-top: 0;
  padding-bottom: 0;
  max-width: unset !important;
}
.hyp-risk-tabs {
  text-transform: capitalize;
}

.hyp-risk-grid-2 {
  display: grid;
  grid-template-columns: 49.5% 49.5%;
  grid-column-gap: 1%;
  min-width: 900px;
  overflow-x: auto;
  grid-template-areas:
    "left right"
    "left right1"
    "left right2"
    "left right3";
}
.hyp-risk-grid-2-left {
  grid-area: left;
}
.hyp-risk-grid-2-leftgrid {
  display: grid;
}
.details-heading {
  font-size: 20px;
}
.hyp-back-to-country-btn {
  height: 48px !important;
  font-size: 0.875rem;
}
.v-data-table--dense > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table--dense > .v-data-table__wrapper > table > thead > tr > td,
.v-data-table--dense > .v-data-table__wrapper > table > tfoot > tr > td {
  height: 20px;
  line-height: 1;
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > td,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
  font-size: 12px;
}
.hyp-risks-content-expanded
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td,
.hyp-risks-content-expanded
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > td,
.hyp-risks-content-expanded
  .v-data-table--dense
  > .v-data-table__wrapper
  > table
  > tfoot
  > tr
  > td {
  height: 40px;
  line-height: 1.5;
}
.hyp-risks-content-expanded
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td,
.hyp-risks-content-expanded
  .v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr
  > td,
.hyp-risks-content-expanded
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tfoot
  > tr
  > td {
  font-size: 14px;
}
.hyp-risks-city-arrivals-cards {
  max-width: 32.5vw !important;
  min-width: 32.5vw !important;
  min-height: 100% !important;
}
.hyp-risks-city-arrival-rating-cards .v-card__text > div {
  font-size: 0.75rem !important;
  font-weight: bold !important;
}
.hyp-risks-city-image {
  max-width: 30vw !important;
}
.hyp-grid-image-container {
  text-align: center;
}
.hyp-risks-content-expanded .hyp-risks-city-image {
  max-width: 50vw !important;
  margin: unset !important;
}
.hyp-hide {
  display: none !important;
}
</style>
